export const shirtSizes = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"]
export const yesNoOptions = [{ text: "Nicht erfasst", value: null }, { text: "Ja", value: true }, { text: "Nein", value: false }]
export const yesNo = value => value === true ? "Ja" : value === false ? "Nein" : "Nicht erfasst"
export const emptyFlight = () => ({ date: "", arrivalDate: "", airline: "", number: "", from: "", to: "", departure: "", arrival: "" })
export const emptyFlights = () => ({ outbound: emptyFlight(), inbound: emptyFlight() })
export const flightSides = [{ key: "outbound", label: "Hinflug" }, { key: "inbound", label: "Rückflug" }]
export function flightChoice(owner, side, personal = false) {
  if (owner.flightRefs && Object.prototype.hasOwnProperty.call(owner.flightRefs, side)) return owner.flightRefs[side]
  if (personal && !owner.flights) return "booking"
  return owner.flights && owner.flights[side] && Object.values(owner.flights[side]).some(Boolean) ? "custom" : ""
}
export function resolveFlight(admin, person, side, catalog = []) {
  let owner = person || admin
  let ref = flightChoice(owner, side, !!person)
  const inherited = ref === "booking"
  if (inherited) { owner = admin; ref = flightChoice(admin, side) }
  if (ref === "custom") return { flight: owner.flights && owner.flights[side] || emptyFlight(), ref, inherited, source: "Eigener Flug" }
  if (!ref) return { flight: emptyFlight(), ref, inherited, source: "Noch nicht zugeordnet" }
  const record = catalog.find(item => item.id === ref && item.side === side)
  return { flight: record ? record.flight : emptyFlight(), ref, inherited, source: record ? "Zentraler Flug" : "Flug nicht verfügbar", missing: !record }
}
export const flightComplete = flight => ["date", "airline", "from", "to", "departure", "arrival"].every(key => !!flight[key])
export const flightsFor = (admin, person, catalog = []) => Object.fromEntries(flightSides.map(side => [side.key, resolveFlight(admin, person, side.key, catalog).flight]))
export function flightProgress(admin, side, catalog = []) {
  const total = admin.persons.length
  const count = admin.persons.filter(person => flightComplete(resolveFlight(admin, person, side, catalog).flight)).length
  return { count, total, complete: total > 0 && count === total }
}
export function flightUsage(rows, id, catalog = []) {
  return rows.filter(row => row.management && !row.missingBooking && !row.deletionPending).reduce((count, row) => count + row.management.persons.filter(person =>
    flightSides.some(side => resolveFlight(row.management, person, side.key, catalog).ref === id)).length, 0)
}
export const formatDate = value => value ? value.slice(0, 10).split("-").reverse().join(".") : ""
const formatTimestamp = value => {
  const date = value ? new Date(value) : null
  return date && Number.isFinite(date.getTime()) ? date.toLocaleDateString("de-CH", { timeZone: "Europe/Zurich" }) : ""
}

function validDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return false
  const date = new Date(`${value}T12:00:00Z`)
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value
}
export function ageAt(birth, start) {
  if (!validDate(birth) || !validDate(start) || birth > start) return null
  return Number(start.slice(0, 4)) - Number(birth.slice(0, 4)) - (start.slice(5) < birth.slice(5) ? 1 : 0)
}
export function birthdayDuring(birth, start, end) {
  if (!validDate(birth) || !validDate(start) || !validDate(end) || end < start || birth > end) return ""
  // Bound the loop even for damaged historical data.
  for (let year = Number(start.slice(0, 4)); year <= Math.min(Number(end.slice(0, 4)), Number(start.slice(0, 4)) + 2); year++) {
    const birthday = `${year}-${birth.slice(5)}`
    if (validDate(birthday) && birthday >= start && birthday <= end && birthday >= birth) return birthday
  }
  return ""
}
export function flightSummary(flight) {
  if (!flight || !Object.values(flight).some(Boolean)) return "Noch nicht erfasst"
  const route = `${flight.from || "?"} ${flight.departure || "–"} → ${flight.to || "?"} ${flight.arrival || "–"}`
  return [formatDate(flight.date), flight.airline, flight.number, route, flight.arrivalDate && flight.arrivalDate !== flight.date ? `Ankunft ${formatDate(flight.arrivalDate)}` : ""].filter(Boolean).join(" · ")
}
export function invoiceDiffers(row) {
  const invoice = row.bill && row.bill.invoice
  if (!invoice) return false
  return JSON.stringify(row.trip) !== JSON.stringify(invoice.trip)
    || row.pricing.dailyPrice !== invoice.pricing.dailyPrice || row.pricing.adults !== invoice.pricing.adults
}

export function exportRows(rows, kind, catalog = []) {
  const list = rows.filter(row => row.management && !row.missingBooking && !row.deletionPending)
  const prefix = ["Nr.", "Buchungs-ID"]
  const flightHeaders = label => ["Zuordnung", "Datum", "Fluggesellschaft", "Flugnummer", "Abflughafen", "Abflugzeit (Ortszeit)", "Zielflughafen", "Ankunftszeit (Ortszeit)", "Ankunftsdatum"].map(field => `${label}: ${field}`)
  const headers = kind === "hotel"
    ? [...prefix, "Anrede", "Vorname", "Nachname", "Anreise", "Abreise", "Zimmer", "Kategorie", "An Hotel weitergegeben", "Weitergegeben am", "Hotelwünsche / Bemerkungen"]
    : kind === "transfer"
      ? [...prefix, "Vorname", "Nachname", ...flightHeaders("Hinflug"), ...flightHeaders("Rückflug")]
      : [...prefix, "Vorname", "Nachname", "T-Shirt-Grösse", "Studio"]
  if (!["hotel", "transfer", "shirts"].includes(kind)) throw new Error("Unbekannter Export.")
  const data = [headers]
  const counts = Object.fromEntries(shirtSizes.map(size => [size, 0]))
  let missing = 0
  const flightValues = flight => [formatDate(flight.date), flight.airline, flight.number, flight.from, flight.departure, flight.to, flight.arrival, formatDate(flight.arrivalDate || flight.date)]
  for (const [index, row] of list.entries()) {
    const admin = row.management
    for (const person of admin.persons) {
      const key = [row.number || index + 1, row.id]
      if (kind === "hotel") data.push([...key, person.salutation, person.firstName, person.lastName,
        formatDate(admin.stay.start), formatDate(admin.stay.end), admin.stay.room, admin.stay.category === "superior" ? "Superior" : "Normal",
        admin.hotelSent ? "Ja" : "Nein", formatTimestamp(admin.hotelSentAt), admin.hotelNotes])
      if (kind === "transfer") {
        const values = flightSides.flatMap(side => {
          const resolved = resolveFlight(admin, person, side.key, catalog)
          if (resolved.missing) throw new Error("Ein zugeordneter Flug ist nicht verfügbar. Bitte die Übersicht aktualisieren und die Zuordnung prüfen.")
          const status = resolved.ref && !flightComplete(resolved.flight) ? `${resolved.source} – Angaben unvollständig` : resolved.source
          return [status, ...flightValues(resolved.flight)]
        })
        data.push([...key, person.firstName, person.lastName, ...values])
      }
      if (kind === "shirts") {
        data.push([...key, person.firstName, person.lastName, person.shirtSize || "Nicht erfasst", admin.studio])
        if (Object.prototype.hasOwnProperty.call(counts, person.shirtSize)) counts[person.shirtSize]++
        else missing++
      }
    }
  }
  if (kind === "shirts") data.push([], ["Grössenübersicht", "Anzahl"], ...Object.entries(counts), ["Fehlend / andere Grösse", missing], ["Personen gesamt", list.reduce((sum, row) => sum + row.management.persons.length, 0)])
  return data
}
